<script setup lang="ts">
import {PropType, ref, watch} from "vue";
import {AddonConfig} from "/@src/components/custom/form/BaseFormField.vue";
import {useFormField} from "/@src/composable/form/useFormField";
import { Collapse } from 'vue-collapsed'
import {VTabsItem} from "/@src/components/base/tabs/VTabs.vue";

export type  FormGroupDirection = 'row'|'column'
const props = defineProps({
  label: String,
  direction: {
    type: String as PropType<FormGroupDirection>,
    default: 'row',
  },
  gap: {
    type: Number,
    default: 4,
  },
  open: {
    type: Boolean,
    default: true,
  },
  canCollapse: {
    type: Boolean,
    default: false,
  },
  labelHighlight: {
    type: Boolean,
    default: false,
  },
  bold: {
    type: Boolean,
    default: true,
  },
  visible:{
    type: Boolean,
    default: true,
  },
  titleHighlight:{
    type: Boolean,
    default: false,
  },
  paddingX:{
    type: Boolean,
    default: false,
  },
  addonConfig:{
    type: Object as PropType<AddonConfig>,
  },
  tabs:{
    type: Array as PropType<VTabsItem[]>,
  },
  divider:{
    type: Boolean,
    default: false,
  },
  columns:{
    type: Number,
  },
})
const isOpen = ref(props.open)
const localModel = ref()
if (props.addonConfig?.field){
  useFormField(localModel,props.addonConfig.field)
}

watch(() => props.open, (newVal) => {
  console.log('open',newVal )
  isOpen.value = newVal
})

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const toggleOpen = () => {
  if (!props.canCollapse) return
  isOpen.value = !isOpen.value
  if (props.canCollapse) {
    emit('update:open', isOpen.value)
  }
}
</script>

<template>
  <transition name="fade-fast">
    <div class="w-full" v-if="visible">
      <div v-if="label" class="flex items-center mb-1 py-1 gap-3"
           :class="[canCollapse&&'cursor-pointer',isOpen&&divider&&!titleHighlight&&'border-solid border-b',titleHighlight&&'bg-zinc-200 dark:bg-zinc-600',paddingX&&'px-4']"
           @click="toggleOpen">
        <h3 v-if="label" class="flex items-center gap-2"
            :class="[labelHighlight&&'has-text-primary',bold&&'font-bold']">
          {{label}}
          <span v-if="canCollapse" class="transition-all cursor-pointer" :class="[isOpen&&'rotate-90']">
              <i
                  aria-hidden="true"
                  class="iconify"
                  data-icon="feather:chevron-right"
              />
            </span>

        </h3>
        <V-SwitchBlock
            v-if="addonConfig?.switcher"
            :label="addonConfig?.switcher.label"
            v-model="localModel"
            thin
            color="primary"
        />
      </div>
      <Collapse :when="isOpen">
        <v-tabs v-if="tabs"
                class="form-tabs mt-2"
                :class="paddingX&&'mx-4'"
                slider
                type="rounded"
                align="centered"
                :selected="tabs[0].value"
                :tabs="tabs">
          <template #tab="{activeValue}">
            <div class="flex flex-col gap-4">
              <slot :value="activeValue"></slot>
            </div>
          </template>

        </v-tabs>
        <div v-else
             class="py-2"
             :class="[
             !columns&&'flex',
             direction === 'row' && 'flex-col',
             'gap-'+gap,
              direction === 'column' && 'items-center',
              paddingX&&'mx-4',
              columns&&`columns-${columns}`,
             ]">

          <slot></slot>

        </div>
      </Collapse>
    </div>
  </transition>

</template>

<style lang="scss">
.form-tabs{
  .tabs-inner {
    .tabs{
      width: 100%;
      max-width: unset;
    }
  }
}
</style>
